import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import {
  atPeriodEnd,
  chargesNow,
  earnsFoundingListener,
  hasPendingChange,
  itemsGained,
  planFor,
  planName,
  upgradeError,
} from '../../../_helpers/planChanges.js';

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12.5 4.2 4.2L19 7" />
  </svg>
);

const Cross = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M7 7l10 10M17 7 7 17" />
  </svg>
);

const Star = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.8l2.4 5.6 6 .5-4.6 3.9 1.4 5.9L12 15.6l-5.2 3.1 1.4-5.9-4.6-3.9 6-.5z" />
  </svg>
);

/** The catalog segment of a kind (`upgrade.modal.title.<kind>`). */
const KIND_KEY = {
  checkout: 'checkout',
  upgrade: 'upgrade',
  switch_now: 'switchNow',
  switch_later: 'switchLater',
  downgrade: 'downgrade',
  cancel: 'cancel',
};

const addInterval = (date, interval) => {
  const next = new Date(date.getTime());
  if (interval === 'year') next.setFullYear(next.getFullYear() + 1);
  else next.setMonth(next.getMonth() + 1);
  return next;
};

/**
 * The confirmation for every plan change on the Upgrade page (ENG-892).
 *
 * A change charged now (an upgrade, monthly to yearly) is priced by
 * `POST /me/subscription/preview` (ENG-891) before anything else: its lines,
 * the total due today and "Pay $X and upgrade". Confirming sends the
 * preview's `prorationDate`, so the charge equals what was shown; an expired
 * preview is priced again. A change at period end charges nothing, shows when
 * it applies and what is given up, and makes "Keep <plan>" the primary
 * action. Checkout shows the full price and goes to Stripe.
 *
 * `role="dialog"`, focused on open; Esc closes it and focus goes back to what
 * opened it. This page shows no broadcast, so the dialog covers none.
 */
const PlanChangeModal = ({ choice, overview, standing, loadPreview, onConfirm, onClose }) => {
  const { t, format } = useLocale();
  const { role, interval, kind } = choice;
  const kindKey = KIND_KEY[kind];
  const later = atPeriodEnd(kind);
  const now = chargesNow(kind);
  const dialogRef = useRef(null);
  const [preview, setPreview] = useState({ status: now ? 'loading' : 'none', data: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const money = (cents) => format.usd((cents ?? 0) / 100, { cents: 'always' });
  const perLabel = (iv) => t(iv === 'year' ? 'upgrade.compare.perYear' : 'upgrade.compare.perMonth');
  const priced = (cents, iv) => t('upgrade.compare.price', { price: money(cents), per: perLabel(iv) });
  const day = (value) => format.date(value, 'short');
  const currentName = planName(t, standing.role);
  const targetName = planName(t, role);
  const targetPlan = planFor(overview, role, interval);

  const price = useCallback(async () => {
    setPreview({ status: 'loading', data: null });
    try {
      const data = await loadPreview();
      setPreview({ status: 'ready', data });
      return data;
    } catch (err) {
      setPreview({ status: 'error', data: null });
      setError(upgradeError(err));
      return null;
    }
  }, [loadPreview]);

  useEffect(() => {
    if (now) price();
    // Price once on open; a re-price is asked for explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();
    return () => { if (opener && typeof opener.focus === 'function') opener.focus(); };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ prorationDate: preview.data?.prorationDate ?? null });
    } catch (err) {
      setError(upgradeError(err));
      if (err?.code === 'subscription_preview_expired') await price();
    } finally {
      setBusy(false);
    }
  };

  let lines = [];
  let dueCents = 0;
  let after = '';
  if (kind === 'checkout') {
    dueCents = targetPlan?.amountCents ?? 0;
    lines = [{ key: 'full', label: t('upgrade.modal.lineFull', { plan: targetName, interval: t(`upgrade.interval.${interval}`) }), amount: money(dueCents) }];
    after = t('upgrade.modal.renews', { date: day(addInterval(new Date(), interval)), price: priced(dueCents, interval) });
  } else if (now && preview.data) {
    const data = preview.data;
    dueCents = data.amountDueCents ?? 0;
    lines = (data.lines || []).map((line, index) => {
      const name = line.role ? planName(t, line.role) : '';
      let label;
      if (line.kind === 'credit' && name) label = t('upgrade.modal.lineCredit', { plan: name });
      else if (name) {
        label = t('upgrade.modal.lineCharge', {
          plan: name,
          interval: t(`upgrade.interval.${line.interval === 'year' ? 'year' : 'month'}`),
          start: format.date(line.periodStart, 'monthDay'),
          end: format.date(line.periodEnd, 'monthDay'),
        });
      } else label = t('upgrade.modal.lineOther');
      return { key: `${index}`, label, amount: money(line.amountCents), credit: line.amountCents < 0 };
    });
    if (data.nextBillingAt && data.nextAmountCents) {
      const nextInterval = kind === 'upgrade' && interval === 'month' && standing.interval === 'year' ? 'month' : interval;
      after = t('upgrade.modal.renewsAt', { price: priced(data.nextAmountCents, nextInterval), date: day(data.nextBillingAt) });
    }
  } else if (later) {
    const when = day(standing.periodEnd);
    if (kind === 'cancel') after = t('upgrade.modal.cancelAfter', { plan: currentName, date: when });
    else if (kind === 'downgrade') {
      after = t('upgrade.modal.downgradeAfter', { plan: currentName, date: when, target: targetName, price: priced(targetPlan?.amountCents, interval) });
    } else after = t('upgrade.modal.renewsAt', { price: priced(targetPlan?.amountCents, interval), date: when });
  }

  const due = money(dueCents);
  const gains = kind === 'checkout' || kind === 'upgrade' ? itemsGained(overview, standing.role, role) : [];
  const losses = kind === 'downgrade' || kind === 'cancel' ? itemsGained(overview, role, standing.role) : [];
  const replaces = now && hasPendingChange(standing);
  const founding = !later && earnsFoundingListener(overview, role);
  const when = later ? day(standing.periodEnd) : '';
  const confirmLabel = t(`upgrade.modal.confirm.${kindKey}`, { price: due });
  const pricing = now && preview.status === 'loading';
  const canConfirm = !busy && !(now && preview.status !== 'ready');

  const title = kind === 'cancel'
    ? t('upgrade.modal.title.cancel', { plan: currentName })
    : t(`upgrade.modal.title.${kindKey}`, { plan: targetName });
  const lead = kind === 'cancel' || kind === 'downgrade'
    ? t('upgrade.modal.lead.downgrade', { plan: currentName, date: when })
    : t(`upgrade.modal.lead.${kindKey}`, { date: when });
  const fine = later
    ? t('upgrade.modal.fineUndo', { date: when })
    : t(kind === 'checkout' ? 'upgrade.modal.fineCheckout' : 'upgrade.modal.fineCharge');

  const confirmButton = (
    <button type="button" className={`upg-btn ${later ? 'upg-btn--ghost' : 'upg-btn--gold'}`} onClick={confirm} disabled={!canConfirm} data-testid="upgrade-modal-confirm">
      {busy ? t('upgrade.modal.working') : confirmLabel}
    </button>
  );

  return (
    <div className="upg-overlay" onKeyDown={onKeyDown}>
      <div
        className="upg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upg-modal-title"
        tabIndex={-1}
        ref={dialogRef}
        data-testid="upgrade-modal"
      >
        <button type="button" className="upg-x" aria-label={t('upgrade.modal.close')} onClick={onClose}>
          <Cross />
        </button>
        <div className={`upg-m-eye${later ? ' upg-m-eye--later' : ''}`}>
          {later ? t('upgrade.modal.scheduledFor', { date: when }) : t('upgrade.modal.effectiveToday')}
        </div>
        <h2 id="upg-modal-title" className="upg-disp">{title}</h2>
        <p className="upg-m-lead">{lead}</p>
        {gains.length > 0 && (
          <div className="upg-m-list upg-m-list--gain">
            <h3>{t('upgrade.modal.unlock')}</h3>
            <ul>{gains.map((item) => <li key={item}><Check /><span>{t(`upgrade.features.${item}`)}</span></li>)}</ul>
          </div>
        )}
        {losses.length > 0 && (
          <div className="upg-m-list upg-m-list--loss">
            <h3>{t('upgrade.modal.giveUp', { date: day(standing.periodEnd) })}</h3>
            <ul>{losses.map((item) => <li key={item}><Cross /><span>{t(`upgrade.features.${item}`)}</span></li>)}</ul>
          </div>
        )}
        <div className="upg-bill" aria-live="polite">
          {pricing && <p className="upg-m-after" role="status">{t('upgrade.modal.pricing')}</p>}
          {lines.map((line) => (
            <div key={line.key} className={`upg-bill-row${line.credit ? ' upg-bill-row--credit' : ''}`} data-testid="upgrade-modal-line">
              <span>{line.label}</span><span>{line.amount}</span>
            </div>
          ))}
          {!pricing && (
            <div className="upg-bill-total">
              <span>{t('upgrade.modal.dueToday')}</span><span data-testid="upgrade-modal-due">{due}</span>
            </div>
          )}
        </div>
        {after && <p className="upg-m-after">{after}</p>}
        {replaces && <p className="upg-m-note">{t('upgrade.modal.replacesScheduled')}</p>}
        {founding && (
          <div className="upg-m-fl"><Star /><span>{t('upgrade.founding.modal')}</span></div>
        )}
        {error && (
          <div className="upg-m-error" role="alert" data-testid="upgrade-modal-error">
            <div>{error.key ? t(error.key) : error.text}</div>
            {error.invoiceUrl && (
              <a href={error.invoiceUrl} target="_blank" rel="noopener noreferrer">{t('upgrade.modal.invoiceLink')}</a>
            )}
          </div>
        )}
        <div className="upg-m-actions">
          {later ? (
            <>
              <button type="button" className="upg-btn upg-btn--gold" onClick={onClose} data-testid="upgrade-modal-keep">
                {t('upgrade.modal.keep', { plan: currentName })}
              </button>
              {confirmButton}
            </>
          ) : (
            <>
              {confirmButton}
              <button type="button" className="upg-btn upg-btn--ghost" onClick={onClose}>{t('upgrade.modal.notNow')}</button>
            </>
          )}
        </div>
        <p className="upg-m-fine">{fine}</p>
      </div>
    </div>
  );
};

export default PlanChangeModal;
